// Tipos/labels do histórico durável das solicitações de leilão (tabela
// `leilao_solicitacao_historico` do backend) — o que sobrevive ao expurgo do
// Redis, incluindo leilões perdidos e os que não disputamos.
// Espelha `ListSolicitacaoHistoricoResponseDto` do backend `consignado-trabalhador`.

import {
  REDIS_SOLICITACAO_STATUSES,
  REDIS_STATUS_COLORS,
  REDIS_STATUS_LABELS,
  RedisSolicitacaoStatus,
} from "./auction-redis.model";

// Status exclusivos do histórico, fora da máquina de status do Redis.
// SEM_FIM_LOTE não é gravado: o backend o deriva de AGUARDANDO_FIM_LOTE quando
// o fim do leilão passa da tolerância sem o webhook de fim-lote.
export const SEM_LANCE_STATUS = "LEILAO_SEM_LANCE" as const;
export const AGUARDANDO_FIM_LOTE_STATUS = "AGUARDANDO_FIM_LOTE" as const;
export const SEM_FIM_LOTE_STATUS = "SEM_FIM_LOTE" as const;
export const FIM_SEM_INICIO_STATUS = "FIM_SEM_INICIO" as const;

export type HistoricoSolicitacaoStatus =
  | RedisSolicitacaoStatus
  | typeof SEM_LANCE_STATUS
  | typeof AGUARDANDO_FIM_LOTE_STATUS
  | typeof SEM_FIM_LOTE_STATUS
  | typeof FIM_SEM_INICIO_STATUS;

// AGUARDANDO_LANCE e LANCE_ENVIADO só existem no Redis: o histórico nunca os grava.
export const HISTORICO_SOLICITACAO_STATUSES: HistoricoSolicitacaoStatus[] = [
  ...REDIS_SOLICITACAO_STATUSES.filter(
    (status) =>
      status !== RedisSolicitacaoStatus.WAITING_BID &&
      status !== RedisSolicitacaoStatus.BID_SENT,
  ),
  SEM_LANCE_STATUS,
  AGUARDANDO_FIM_LOTE_STATUS,
  SEM_FIM_LOTE_STATUS,
  FIM_SEM_INICIO_STATUS,
];

export const HISTORICO_STATUS_LABELS: Record<HistoricoSolicitacaoStatus, string> = {
  ...REDIS_STATUS_LABELS,
  [SEM_LANCE_STATUS]: "Sem lance",
  [AGUARDANDO_FIM_LOTE_STATUS]: "Aguardando fim do lote",
  [SEM_FIM_LOTE_STATUS]: "Fim do lote não recebido",
  [FIM_SEM_INICIO_STATUS]: "Início do lote não recebido",
};

export const HISTORICO_STATUS_COLORS: Record<HistoricoSolicitacaoStatus, string> = {
  ...REDIS_STATUS_COLORS,
  [SEM_LANCE_STATUS]: "gray",
  [AGUARDANDO_FIM_LOTE_STATUS]: "yellow",
  [SEM_FIM_LOTE_STATUS]: "orange",
  [FIM_SEM_INICIO_STATUS]: "orange",
};

// Espelha o enum `MotivoSemLance` do backend.
export enum MotivoSemLance {
  COM_GARANTIA = "COM_GARANTIA",
  EMPREGADOR_NAO_CNPJ = "EMPREGADOR_NAO_CNPJ",
  MARGEM_BAIXA = "MARGEM_BAIXA",
  PRAZO_ALTO = "PRAZO_ALTO",
  PEP = "PEP",
  AFASTAMENTO = "AFASTAMENTO",
  IDADE_FORA_FAIXA = "IDADE_FORA_FAIXA",
  VINCULO_INSUFICIENTE = "VINCULO_INSUFICIENTE",
  SEM_FAIXA_TAXA = "SEM_FAIXA_TAXA",
  SIMULACAO_INVALIDA = "SIMULACAO_INVALIDA",
  LANCE_REJEITADO = "LANCE_REJEITADO",
  FALHA_TECNICA = "FALHA_TECNICA",
}

export const MOTIVO_SEM_LANCE_LABELS: Record<MotivoSemLance, string> = {
  [MotivoSemLance.COM_GARANTIA]: "Solicitação com garantia",
  [MotivoSemLance.EMPREGADOR_NAO_CNPJ]: "Empregador não-CNPJ",
  [MotivoSemLance.MARGEM_BAIXA]: "Margem abaixo do mínimo (R$ 45)",
  [MotivoSemLance.PRAZO_ALTO]: "Prazo acima do máximo (80 parcelas)",
  [MotivoSemLance.PEP]: "Pessoa Exposta Politicamente",
  [MotivoSemLance.AFASTAMENTO]: "Alerta de afastamento",
  [MotivoSemLance.IDADE_FORA_FAIXA]: "Idade fora da faixa (19–65 anos)",
  [MotivoSemLance.VINCULO_INSUFICIENTE]: "Tempo de vínculo insuficiente",
  [MotivoSemLance.SEM_FAIXA_TAXA]: "Sem faixa de taxa elegível",
  [MotivoSemLance.SIMULACAO_INVALIDA]: "Simulação do lance inválida",
  [MotivoSemLance.LANCE_REJEITADO]: "Lance rejeitado pela BMP",
  [MotivoSemLance.FALHA_TECNICA]: "Falha técnica ao enviar o lance",
};

// Espelha a entity do back, menos o `snapshot` (excluído da listagem). Os
// campos são anuláveis porque a tabela recebe upsert parcial em cada caminho
// de escrita (início do lote, fim do lote e transições do lance).
export interface HistoricoSolicitacaoItem {
  numeroSolicitacao: number;
  codigoSolicitacaoLeilao: string | null;
  codigoLote: string | null;
  cpf: string | null;
  matricula: string | null;
  nomeTrabalhador: string | null;
  dataNascimento: string | null;
  dataAdmissao: string | null;
  pessoaExpostaPoliticamenteCodigo: number | null;
  pessoaExpostaPoliticamenteDescricao: string | null;
  inscricaoEmpregadorCodigo: number | null;
  inscricaoEmpregadorDescricao: string | null;
  numeroInscricaoEmpregador: string | null;
  valorLiberadoSolicitado: number | null;
  nroParcelasSolicitado: number | null;
  margemDisponivel: number | null;
  elegivelEmprestimo: boolean | null;
  dataHoraValidadeSolicitacao: string | null;
  alertas: unknown[] | null;
  status: HistoricoSolicitacaoStatus;
  ganhou: boolean;
  motivoSemLance: MotivoSemLance | null;
  codigoErroLance: string | null;
  mensagemErroLance: string | null;
  idProposta: string | null;
  codigoRequisicao: string | null;
  valorTaxaMensal: number | null;
  processorId: string | null;
  dataHoraInicioLeilao: string | null;
  dataHoraFimLeilao: string | null;
  codigoLanceLeilao: string | null;
  lanceTaxaMensal: number | null;
  lanceValorLiberado: number | null;
  lanceNumeroParcelas: number | null;
  posicaoFinal: number | null;
  taxaVencedora: number | null;
  lanceDataHoraInclusao: string | null;
  criadoEmRedis: string | null;
  atualizadoEmRedis: string | null;
  createdAt: string;
  updatedAt: string;
}

// Resposta paginada (snake_case espelha o wire do backend).
export interface HistoricoSolicitacaoListResponse {
  data: HistoricoSolicitacaoItem[];
  total: number;
  pagina_atual: number;
  tamanho_pagina: number;
  total_paginas: number;
  pagina_anterior: number | null;
  proxima_pagina: number | null;
}

export interface HistoricoSolicitacaoListParams {
  page: number;
  pageSize: number;
  searchTerm?: string;
  statuses?: HistoricoSolicitacaoStatus[];
  codigoLote?: string;
  motivoSemLance?: MotivoSemLance;
  codigoErroLance?: string;
  dataInicial?: string;
  dataFinal?: string;
}

export interface HistoricoSolicitacaoFilters {
  searchTerm: string;
  statuses: HistoricoSolicitacaoStatus[];
  codigoLote: string;
  motivoSemLance: MotivoSemLance | "";
  codigoErroLance: string;
  dataInicial: string;
  dataFinal: string;
}

export const INITIAL_HISTORICO_FILTERS: HistoricoSolicitacaoFilters = {
  searchTerm: "",
  statuses: [],
  codigoLote: "",
  motivoSemLance: "",
  codigoErroLance: "",
  dataInicial: "",
  dataFinal: "",
};
